import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CasinoIcon from "@mui/icons-material/Casino";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import { Box, CircularProgress, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import SoundPreset from "./SoundPreset";
import { useTranslation } from "react-i18next";
import { useSound } from "../../../../../context/juno/SoundContext";
import { modelRequirements } from "../../../../../utils/validation/soundValidation";
import { useAlert } from "../../../../../context/AlertContext";
import { GradationButton } from "../../../../../utils/gradationButton";
import { RowBox } from "../../../../../utils/styledBox";

interface InputPromptProps {
  setGenerationCompleted: (completed: boolean) => void;
}

const InputPrompt: React.FC<InputPromptProps> = ({ setGenerationCompleted }) => {
  const { t } = useTranslation();
  const { prompt, setPrompt, aiEngine, submitSound, credit, isCalculatingCredit } = useSound();
  const { setAlert } = useAlert();
  const [localPrompt, setLocalPrompt] = useState<string>("");
  // エラーメッセージを状態として保持（UIに表示するため）
  const [disable3sec, setDisable3sec] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingType, setUpdatingType] = useState<"dice" | "brush" | null>(null);
  const [rotationDegree, setRotationDegree] = useState(0);

  // プロンプト生成APIへのリクエスト（ダイス）
  const generateSampleText = async () => {
    try {
      setUpdating(true);
      setUpdatingType("dice");
      setRotationDegree(rotationDegree + 360);
      setIsProcessing(true);

      const url = "/api/v1/juno/sound/generate-prompt";
      const csrftoken = Cookies.get("csrftoken");
      const headers = new Headers({
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken!,
      });

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          soundType: "music",
          model: aiEngine,
        }),
      });

      const data = await response.json();
      if (data.prompt) {
        setPrompt(data.prompt);
      }
    } catch (e) {
      console.error(e);
      setAlert("error", t("juno.sound.error.prompt.generate"));
    } finally {
      setIsProcessing(false);
      setUpdating(false);
      setUpdatingType(null);
    }
  };

  // プロンプト生成APIへのリクエスト（マジックブラシ）
  const updatePrompt = async () => {
    if (!localPrompt.trim()) return;

    try {
      setUpdating(true);
      setUpdatingType("brush");
      setIsProcessing(true);

      const url = "/api/v1/juno/sound/update-prompt";
      const csrftoken = Cookies.get("csrftoken");
      const headers = new Headers({
        "Content-Type": "application/json",
        "X-CSRFToken": csrftoken!,
      });

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          prompt: localPrompt,
          soundType: "music",
          model: aiEngine,
        }),
      });

      const data = await response.json();
      if (data.prompt) {
        setPrompt(data.prompt);
      }
    } catch (e) {
      console.error(e);
      setAlert("error", t("juno.sound.error.prompt.update"));
    } finally {
      setIsProcessing(false);
      setUpdating(false);
      setUpdatingType(null);
    }
  };

  // 外部からのプロンプト変更をローカルに反映
  useEffect(() => {
    setLocalPrompt(prompt);
  }, [prompt]);

  // 3秒間のボタン無効化処理
  useEffect(() => {
    if (disable3sec) {
      const timer = setTimeout(() => {
        setDisable3sec(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [disable3sec]);

  // ローカルプロンプト変更時の処理
  const handleLocalPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalPrompt(newValue);
  };

  // フォーカスアウト時の処理
  const handlePromptBlur = () => {
    setPrompt(localPrompt);
  };

  // 送信処理
  const handleSubmit = async () => {
    setIsProcessing(true);
    setDisable3sec(true);
    try {
      await submitSound(
        setGenerationCompleted,
        (errorKey) => {
          if (errorKey) {
            setAlert("error", t(errorKey));
          }
        },
        setAlert
      );
    } finally {
      setDisable3sec(false);
      setIsProcessing(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
        borderRadius: 2,
        p: 2,
        mb: 2,
        zIndex: 1,
        position: "sticky",
      }}
    >
      <RowBox sx={{ gap: 2, alignItems: "flex-end", mb: 1 }}>
        <TextField
          variant="standard"
          autoComplete={"off"}
          placeholder={
            !modelRequirements[aiEngine].prompt
              ? t("juno.sound.params.input.notRequired")
              : t("juno.sound.params.input.placeholder")
          }
          autoFocus
          value={localPrompt}
          onChange={handleLocalPromptChange}
          onBlur={handlePromptBlur}
          multiline
          fullWidth
          maxRows={8}
          disabled={!modelRequirements[aiEngine].prompt}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="body2" color="secondary" sx={{ ml: 1, display: { xs: "none", sm: "block" } }}>
                  @{aiEngine}:
                </Typography>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              backgroundColor: "transparent",
              overflow: "hidden",
            },
            inputProps: { maxLength: aiEngine === "ELEVENLABS" ? 450 : 1000 },
          }}
          sx={{
            "& .MuiInput-root": {
              "&:before, &:after": {
                border: "none",
                display: "none",
              },
            },
          }}
        />
      </RowBox>
      <RowBox sx={{ gap: 2, justifyContent: "space-between", mt: 0.5 }}>
        <Box sx={{ gap: 2, display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
            {/* サイコロアイコン（プロンプト生成） */}
            <Tooltip title={t("juno.sound.inspire.dice")}>
              <span>
                <IconButton
                  onClick={generateSampleText}
                  disabled={localPrompt.length > 0 || updating || isProcessing || !modelRequirements[aiEngine].prompt}
                >
                  <CasinoIcon
                    sx={{
                      transform: `rotate(${rotationDegree}deg)`,
                      transition: "transform 1s ease-in-out",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            {/* マジックブラシアイコン（プロンプト強化） */}
            <Tooltip title={t("juno.sound.inspire.improve")}>
              <span>
                <IconButton
                  onClick={updatePrompt}
                  disabled={
                    updating || isProcessing || localPrompt.trim().length === 0 || !modelRequirements[aiEngine].prompt
                  }
                >
                  {updating && updatingType === "brush" ? (
                    <CircularProgress size={24} color="primary" />
                  ) : (
                    <AutoFixHighIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {/* インスピレーションボタン */}
            <SoundPreset
              prompt={localPrompt}
              setPrompt={(newPrompt: string) => {
                setLocalPrompt(newPrompt);
                setPrompt(newPrompt);
              }}
              position="bottom"
              disabled={updating || isProcessing || !modelRequirements[aiEngine].prompt}
            />

            {/* 削除ボタン */}
            <Tooltip title={t("common.delete")}>
              <span>
                <IconButton
                  onClick={() => {
                    setLocalPrompt("");
                    setPrompt("");
                  }}
                  disabled={!modelRequirements[aiEngine].prompt}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Typography
            variant="caption"
            color={localPrompt.length > (aiEngine === "ELEVENLABS" ? 450 : 1000) ? "error" : "textSecondary"}
          >
            {localPrompt.length}/{aiEngine === "ELEVENLABS" ? 450 : 1000}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" } }}>
          {t("juno.sound.params.input.warning")}
        </Typography>

        <GradationButton
          onClick={handleSubmit}
          sx={{ minWidth: { xs: 40, sm: 240 }, px: 2 }}
          startIcon={disable3sec || isProcessing ? <CircularProgress size={20} /> : <SendIcon />}
          disabled={disable3sec || isProcessing}
        >
          <Typography sx={{ display: { xs: "none", sm: "block" } }}>{t("juno.sound.params.submit")}</Typography>
          {isCalculatingCredit ? <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} /> : `(${credit})`}
        </GradationButton>
      </RowBox>
    </Box>
  );
};

export default InputPrompt;
